"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPMSeverity = exports.getVOCSeverity = exports.getCO2Severity = exports.getIaqSeverity = exports.getMainsPowerSeverity = exports.MAINS_FRACTION_CRITICAL = exports.MAINS_FRACTION_MEDIUM = exports.DEFAULT_MAINS_RATING_AMPS_PER_LEG = exports.getECSeverity = exports.EC_TO_TDS_FACTOR = exports.getBranchCurrentSeverity = exports.resolveBreakerAmperage = exports.BRANCH_CT_FRACTION_CRITICAL = exports.BRANCH_CT_FRACTION_HIGH = exports.BRANCH_CT_FRACTION_MEDIUM = void 0;
const AirQuality_1 = require("./AirQuality");
const BranchCircuit_1 = require("./electrical/BranchCircuit");
const SensorAlert_1 = require("./SensorAlert");
const WaterQualityStatus_1 = require("./water/WaterQualityStatus");
// ── Branch circuit current (HIGH_BCT_CURRENT) ──────────────────────────────
// Severity is a fraction of the breaker amperage — a 15A breaker alarms at
// 12A (Medium), 14.25A (High), 15A+ (Critical). If the user has set a
// per-circuit override (overCurrentAlertCustomAmps), that becomes the
// Medium-severity floor; High/Critical scale up from there.
exports.BRANCH_CT_FRACTION_MEDIUM = 0.8;
exports.BRANCH_CT_FRACTION_HIGH = 0.95;
exports.BRANCH_CT_FRACTION_CRITICAL = 1.0;
const breakerTypeToAmperage = {
    [BranchCircuit_1.BreakerType.AMP_15]: 15,
    [BranchCircuit_1.BreakerType.AMP_20]: 20,
    [BranchCircuit_1.BreakerType.AMP_30]: 30,
    [BranchCircuit_1.BreakerType.AMP_40]: 40,
    [BranchCircuit_1.BreakerType.AMP_70]: 70,
};
/** Resolve effective breaker amperage from a circuit, preferring the numeric field. */
function resolveBreakerAmperage(circuit) {
    if (circuit.breakerAmperage != null)
        return circuit.breakerAmperage;
    if (circuit.breakerType)
        return breakerTypeToAmperage[circuit.breakerType] ?? null;
    return null;
}
exports.resolveBreakerAmperage = resolveBreakerAmperage;
/**
 * Determine alert severity for a measured branch CT amperage.
 * Returns null when no breach (caller should skip alert creation).
 *
 * If the circuit has overCurrentAlertCustomAmps, that user-configured trip
 * point is the Medium floor — High and Critical are computed against the
 * breaker rating so a customized trip still respects the breaker.
 */
function getBranchCurrentSeverity(amps, circuit) {
    const breakerAmps = resolveBreakerAmperage(circuit);
    if (breakerAmps == null || breakerAmps <= 0)
        return null;
    if (amps >= breakerAmps * exports.BRANCH_CT_FRACTION_CRITICAL)
        return SensorAlert_1.SensorAlertSeverity.Critical;
    if (amps >= breakerAmps * exports.BRANCH_CT_FRACTION_HIGH)
        return SensorAlert_1.SensorAlertSeverity.High;
    const mediumFloor = circuit.overCurrentAlert && circuit.overCurrentAlertCustomAmps != null
        ? circuit.overCurrentAlertCustomAmps
        : breakerAmps * exports.BRANCH_CT_FRACTION_MEDIUM;
    if (amps >= mediumFloor)
        return SensorAlert_1.SensorAlertSeverity.Medium;
    return null;
}
exports.getBranchCurrentSeverity = getBranchCurrentSeverity;
// ── Water quality EC (HIGH_EC) ─────────────────────────────────────────────
// Bands come from WaterQualityThreshold (TDS ppm; sensor reports EC in
// µS/cm; convert via the standard 0.6 factor before comparing).
exports.EC_TO_TDS_FACTOR = 0.6;
function getECSeverity(ec) {
    const tds = ec * exports.EC_TO_TDS_FACTOR;
    if (tds > WaterQualityStatus_1.WaterQualityThreshold.concerning)
        return SensorAlert_1.SensorAlertSeverity.High;
    if (tds > WaterQualityStatus_1.WaterQualityThreshold.hard)
        return SensorAlert_1.SensorAlertSeverity.Medium;
    return null;
}
exports.getECSeverity = getECSeverity;
// ── Whole-home / mains power draw (HIGH_MCT_CURRENT) ───────────────────────
// There is NO panel/service rating on the sensor data model (Sensor carries
// only per-branch breakerAmperage), so per-leg mains draw is compared against
// a conservative default residential service rating. FLAG: if a mains/panel
// rating is ever added to the Sensor config, thread it into `ratingPerLeg`.
// Mirrors the branch-circuit fractions but with only two tiers (the ticket
// specifies 80% Medium / 95% Critical for mains).
exports.DEFAULT_MAINS_RATING_AMPS_PER_LEG = 200;
exports.MAINS_FRACTION_MEDIUM = 0.8;
exports.MAINS_FRACTION_CRITICAL = 0.95;
function getMainsPowerSeverity(amps, ratingPerLeg = exports.DEFAULT_MAINS_RATING_AMPS_PER_LEG) {
    if (!Number.isFinite(amps) || ratingPerLeg <= 0)
        return null;
    if (amps >= ratingPerLeg * exports.MAINS_FRACTION_CRITICAL)
        return SensorAlert_1.SensorAlertSeverity.Critical;
    if (amps >= ratingPerLeg * exports.MAINS_FRACTION_MEDIUM)
        return SensorAlert_1.SensorAlertSeverity.Medium;
    return null;
}
exports.getMainsPowerSeverity = getMainsPowerSeverity;
// ── Air quality (HIGH_IAQ / HIGH_CO2 / HIGH_VOC / HIGH_PM) ──────────────────
// Bands come from AirQualityThresholds (BME688/BSEC-derived scales — see the
// long note in AirQuality.ts; do NOT re-apply atmospheric reference values).
// Medium above the "moderate" band start; Critical above the "poor"/heavier
// band. Callers MUST suppress evaluation while the sensor is calibrating and
// skip IAQ readings above 500 (raw gas resistance leaking through).
function getIaqSeverity(iaq) {
    if (!Number.isFinite(iaq))
        return null;
    if (iaq > AirQuality_1.AirQualityThresholds.iaq.severelyPolluted)
        return SensorAlert_1.SensorAlertSeverity.Critical; // > 350
    if (iaq > AirQuality_1.AirQualityThresholds.iaq.moderatelyPolluted)
        return SensorAlert_1.SensorAlertSeverity.Medium; // > 200
    return null;
}
exports.getIaqSeverity = getIaqSeverity;
function getCO2Severity(co2Equivalent) {
    if (!Number.isFinite(co2Equivalent))
        return null;
    if (co2Equivalent > AirQuality_1.AirQualityThresholds.co2.poor)
        return SensorAlert_1.SensorAlertSeverity.Critical; // > 6000
    if (co2Equivalent > AirQuality_1.AirQualityThresholds.co2.moderate)
        return SensorAlert_1.SensorAlertSeverity.Medium; // > 3500
    return null;
}
exports.getCO2Severity = getCO2Severity;
function getVOCSeverity(breathVoc) {
    if (!Number.isFinite(breathVoc))
        return null;
    if (breathVoc > AirQuality_1.AirQualityThresholds.voc.poor)
        return SensorAlert_1.SensorAlertSeverity.Critical; // > 999
    if (breathVoc > AirQuality_1.AirQualityThresholds.voc.moderate)
        return SensorAlert_1.SensorAlertSeverity.Medium; // > 600
    return null;
}
exports.getVOCSeverity = getVOCSeverity;
function getPMSeverity(pm) {
    if (!Number.isFinite(pm))
        return null;
    // Only a "moderate" band is defined for PM2.5 — single Medium tier.
    if (pm > AirQuality_1.AirQualityThresholds.pm25.moderate)
        return SensorAlert_1.SensorAlertSeverity.Medium; // > 0.68
    return null;
}
exports.getPMSeverity = getPMSeverity;
